import { parseISO, format, isSameDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz'
import { Settings } from '@/app/lib/settings';

const EST_TIMEZONE = 'America/New_York';

export const toRelativeSameSiteUrl = (url) => {
	if (typeof url !== 'string' || !url.startsWith('http')) return url;

	try {
		const { host, pathname, search, hash } = new URL(url);
		if (Settings.sameSiteHosts.includes(host)) {
			return `${pathname}${search}${hash}`;
		}
	} catch {
		// Keep the original URL when parsing fails.
	}

	return url;
};

export const parseHashLink = (href) => {
	if (typeof href !== 'string') return null;

	const hashIndex = href.indexOf('#');
	if (hashIndex === -1) return null;

	const hash = href.slice(hashIndex + 1);
	if (!hash) return null;

	return {
		rawPath: href.slice(0, hashIndex),
		hash,
	};
};

export const getLinkPathname = (rawPath) => {
	if (!rawPath) return '';

	const cleaned = rawPath.replace(/\/$/, '');
	if (!cleaned.startsWith('http')) return cleaned;

	try {
		return new URL(cleaned).pathname.replace(/\/$/, '');
	} catch {
		return cleaned;
	}
};

export const formatLink = (page) => {
	let url = '#';
	if (!page) return url;


	if (page.link_type === 'Document' && page.uid && !page.isBroken) {
		if (page.type === 'project') url = formatURL(`${Settings.projectsPrefix}/${page.uid}`);
		else if (page.type === 'story') url = formatURL(`${Settings.storiesPrefix}/${page.uid}`);
		else if (page.type === 'event') url = formatURL(`${Settings.eventsPrefix}/${page.uid}`);
		else if (page.type === 'highlight' && page?.data?.event?.uid) url = formatURL(`${Settings.eventsPrefix}/${page?.data?.event?.uid}/highlights/${page.uid}`);
		else if (page.type === 'session' && page?.data?.event?.uid) url = formatURL(`${Settings.eventsPrefix}/${page?.data?.event?.uid}/session/${page.uid}`);
		else if (page.type === 'highlight') url = formatURL(`${Settings.highlightsPrefix}/${page.uid}`);
		else url = formatURL(page.uid);

	} else if ((page.link_type === 'Web' || page.link_type === 'Media') && page.url) {
		if (page.url.startsWith('http')) {
			url = page.url;
		} else if (page.url.startsWith('/') || page.url.startsWith('#')) {
			url = page.url;
		} else {
			url = 'https://' + page.url;
		}

	} else if (typeof page === 'string') {
		if (page.startsWith('http')) {
			url = page;
		} else if (page.startsWith('/') || page.startsWith('#')) {
			url = page;
		} else {
			url = formatURL(page);
		}

	} else {
		url = formatURL(page);

	}

	return toRelativeSameSiteUrl(url);
};

export const formatURL = (url, prefixPath = true) => {
	if (typeof url !== 'string') return '';
	url = (url === 'homepage') ? '/' : url;

	// Remove trailing slash if it exists
	url = url.endsWith('/') && url !== '/' ? url.slice(0, -1) : url;

	// prefix domain path
	if (prefixPath) url = '/' + url;

	// Ensure there are no double slashes (except the one in "http://")
	url = url.replace(/(https?:\/\/)|(\/{2,})/g, (match, p1) => p1 || '/');

	return url;
};

export const formatTarget = (page) => {
	if (!page) return '_self';
	if (page.link_type === 'Web') return page.target || '_self';
	if (page.link_type === 'Media') return '_blank';
	if (page.link_type === 'Document') return page.target || '_self';
	if (page?.toString()?.startsWith('http')) return '_blank';
	return '_self';
};

export const matchesRoute = (route, uid) => {
	return (
		route === uid ||
		(typeof route === 'string' && route.includes('.') && route.split('.')[0] === uid)
	);
};

export const formatDate = (dateString, formatStr = 'dd.MM.yyyy') => {
	const date = new Date(dateString);
	// formatInTimeZone formats the date object according to the specified timezone
	return formatInTimeZone(date, EST_TIMEZONE, formatStr);
};

export const formatTime = (dateString, formatStr = 'HH:mm') => {
	const date = new Date(dateString);
	return formatInTimeZone(date, EST_TIMEZONE, formatStr);
};

export function formatEventDateRange(startStr, endStr, formatType = 'range-uppercase') {
	const startDate = startStr ? parseISO(startStr) : null;
	const endDate = endStr ? parseISO(endStr) : null;

	const isStartDateValid = startDate && !isNaN(startDate.getTime());
	const isEndDateValid = endDate && !isNaN(endDate.getTime());
	const isMonthLong = formatType === 'month-long';
	const singleDateFormat = isMonthLong
		? 'MMMM d, yyyy'
		: 'MMM d, yyyy';

	if (isStartDateValid && !isEndDateValid) {
		return format(startDate, singleDateFormat);
	}

	if (!isStartDateValid && isEndDateValid) {
		return format(endDate, singleDateFormat);
	}

	if (isStartDateValid && isEndDateValid) {

		if (isSameDay(startDate, endDate)) {
			return format(startDate, singleDateFormat);
		}

		if (isMonthLong) {
			const startPart = format(startDate, 'MMMM d, yyyy');
			const endPart = format(endDate, 'MMMM d, yyyy');
			return `${startPart} - ${endPart}`;
		}

		const startPart = format(startDate, 'MMM d').toUpperCase();
		const endPart = format(endDate, 'MMM d yyyy').toUpperCase();

		return `${startPart} - ${endPart}`;

	}

	return '';
}

const isDateOnly = (str) => typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str.trim());

export const formatEventStartDay = (startStr) => {

	if (!startStr) {
		return null;
	}

	const startTime = parseISO(startStr);
	const isStartTimeValid = !isNaN(startTime.getTime());

	if (!isStartTimeValid) {
		return null;
	}

	// Date-only (no time): show the calendar day in EST without timezone shift
	if (isDateOnly(startStr)) {
		const y = startTime.getUTCFullYear();
		const m = startTime.getUTCMonth();
		const d = startTime.getUTCDate();
		const noonUtc = new Date(Date.UTC(y, m, d, 12, 0, 0));
		return formatInTimeZone(noonUtc, EST_TIMEZONE, 'MMM dd').toUpperCase();
	}

	const formattedStartTime = formatInTimeZone(startTime, EST_TIMEZONE, 'MMM dd');
	return formattedStartTime.toUpperCase();
};

export const formatEventTimeRange = (startStr, endStr) => {
	if (!startStr) {
		return null;
	}

	const startTime = parseISO(startStr);
	const isStartTimeValid = !isNaN(startTime.getTime());

	if (!isStartTimeValid) {
		console.warn("Invalid start time string for formatting:", startStr);
		return null;
	}

	// Format the start time in EST
	const formattedStartTime = formatInTimeZone(startTime, EST_TIMEZONE, 'h:mm a');

	if (endStr) {
		const endTime = parseISO(endStr);
		const isEndTimeValid = !isNaN(endTime.getTime());

		if (isEndTimeValid) {
			// Format the end time in EST
			const formattedEndTime = formatInTimeZone(endTime, EST_TIMEZONE, 'h:mm a');
			return `${formattedStartTime} – ${formattedEndTime}`;
		} else {
			console.warn("Invalid end time string for formatting:", endStr);
		}
	}

	return formattedStartTime;
};

export const sortEventsByDate = (events) => {
	if (!events || !Array.isArray(events)) {
		console.warn("sortEventsByDate received invalid or empty events array.");
		return [];
	}

	const filteredEvents = events.filter(event => {
		const hasValidDate = event?.start_date_and_time && !isNaN(parseISO(event.start_date_and_time).getTime());

		if (!hasValidDate) {
			// console.warn(`Invalid or missing 'start_date_and_time' for event:`, event);
		}

		return hasValidDate;
	});

	filteredEvents.sort((a, b) => {
		const timeA = parseISO(a.start_date_and_time).getTime();
		const timeB = parseISO(b.start_date_and_time).getTime();
		return timeA - timeB;
	});

	return filteredEvents;
};

export const groupEventsByDate = (events, dateFilter = null, venueFilter = null) => {
	const grouped = {};

	if (!events || !Array.isArray(events)) {
		return [];
	}

	// Helper to format in EST
	const formatEst = (date, fmt) => formatInTimeZone(date, EST_TIMEZONE, fmt);

	events.forEach(event => {
		const venueName = event?.venue?.data?.name;

		if (dateFilter) {
			// Must use formatInTimeZone here to ensure displayDate is based on the EST day
			const displayDate = event?.start_date_and_time
				? formatEst(parseISO(event.start_date_and_time), 'EEEE MMM d')
				: null;

			if (displayDate !== dateFilter) {
				return;
			}
		}

		if (venueFilter && venueName !== venueFilter) {
			return;
		}

		if ((!event?.only_show_on_full_list) && event?.start_date_and_time) {
			const dateTime = parseISO(event.start_date_and_time);
			if (isNaN(dateTime.getTime())) {
				console.warn(`Invalid date_and_time for event: ${event.title}`, event.start_date_and_time);
				return;
			}

			// CRITICAL: The dateKey must be calculated based on the EST date 
			const dateKey = formatEst(dateTime, 'yyyy-MM-dd');

			if (!grouped[dateKey]) {
				grouped[dateKey] = {
					// The displayDate must also be formatted based on the EST day
					displayDate: formatEst(dateTime, 'EEEE MMM d'),
					events: [],
				};
			}
			grouped[dateKey].events.push(event);
		}
	});

	const sortedDateGroups = Object.keys(grouped)
		.sort() // Sorting the 'yyyy-MM-dd' keys ensures correct chronological order
		.map(dateKey => grouped[dateKey]);

	sortedDateGroups.forEach(group => {
		group.events.sort((a, b) => {
			// Internal sorting remains correct using raw UTC timestamps
			const timeA = parseISO(a.start_date_and_time).getTime();
			const timeB = parseISO(b.start_date_and_time).getTime();
			return timeA - timeB;
		});
	});

	return sortedDateGroups;
};

export const sliceStyle = (object, withBg = false) => {
	const customStyles = {};

	if (object?.bottom_margin) {
		customStyles.marginBottom = object?.bottom_margin === 'None' ? 0 :
			object?.bottom_margin === 'Small' ? '1rem' :
				object?.bottom_margin === 'Medium' ? '2.5rem' :
					object?.bottom_margin === 'Large' ? '5rem' : '0';
	}

	if (object?.bottom_padding) {
		customStyles.paddingBottom = object?.bottom_padding === 'None' ? 0 :
			object?.bottom_padding === 'Small' ? '4rem' :
				object?.bottom_padding === 'Medium' ? '8rem' :
					object?.bottom_padding === 'Large' ? '10rem' :
						'0';
	}

	if (object?.padding) {
		customStyles.paddingBottom = object?.padding === 'None' ? 0 :
			object?.padding === 'Tiny' ? '1rem' :
				object?.padding === 'Small' ? '4rem' :
					object?.padding === 'Medium' ? '8rem' :
						object?.padding === 'Large' ? '10rem' :
							'0';
	}

	if (object?.text_color) {
		customStyles.color = object?.text_color;
	}

	if (withBg) {
		if (object?.background_color) {
			customStyles.backgroundColor = object.background_color;
		}

		if (object?.background_image?.url) {
			customStyles.backgroundImage = `url(${object.background_image.url})`;
			customStyles.backgroundRepeat = "no-repeat";
			customStyles.backgroundSize = "cover"; // zoom in slightly
			customStyles.backgroundPosition = "center"; // center
		}
	}

	return customStyles;
};

export const getShowOnClassName = (slice, styles = {}) => {
	const showOn = slice?.primary?.show_on;

	if (showOn === 'Desktop only') return styles.desktop_only;
	if (showOn === 'Mobile only') return styles.mobile_only;

	return undefined;
};

export const isHoverCapable = () => {
	return typeof window !== "undefined" &&
		window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};

export const dashify = (text, options = {}) => {
	const separator = options.separator === '_' ? '_' : '-';

	if (typeof text === 'string') {
		return text.trim()
			.replace(/&/g, 'and')
			.replace(/([a-z])([A-Z])/g, `$1${separator}$2`)
			.replace(/\W/g, m => /[À-ž]/.test(m) ? m : separator)
			.replace(new RegExp(`^${separator}+|${separator}+$`, 'g'), '')
			.replace(new RegExp(`${separator}{2,}`, 'g'), m => options && options.condense ? separator : m);
	}
}

export const handleExpiredTokenError = (error) => {
	if (!error) return;

	const errorMessage = error?.message || error?.toString() || String(error);
	const errorString = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
	const lowerErrorString = errorString.toLowerCase();

	if (lowerErrorString.includes('token has expired') || lowerErrorString.includes('preview token has expired')) {
		// Remove the cookie if we're in a browser environment
		if (typeof document !== 'undefined') {
			document.cookie = 'io.prismic.preview=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

			// Reload the page if possible
			if (typeof window !== 'undefined' && window.location) {
				window.location.reload();
			}
		}
	}
}